import { type FC, type ReactNode, useCallback, useEffect } from "react";
import { useUserStore } from "@/stores/useUserStore.ts";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { SignalWrapper } from "@/helpers/signalr.ts";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { toastAndError, toastAndWarn } from "@/helpers/toast.ts";
import { log } from "@/helpers/console.ts";
import { InternalSigRErrors, SigRInvoke } from "@/types/signalr-functions.ts";
import { toast } from "sonner";
import { TutorStatus } from "@/types/tutor.ts";
import { PRODUCTION } from "@/constants/settings.ts";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";

interface SignalRProps {
  children?: ReactNode;
}

export const SignalRWrapper: FC<SignalRProps> = ({ children }) => {
  const { settings } = useSettingsStore();
  const {
    Startup,
    auth,
    userId,
    tutorId,
    valid,
    Logout,
    SetStatus: SetUserStatus,
  } = useUserStore();
  const { Create, Remove, SetStatus, signalr } = useSignalStore();

  const DebugSigR = !settings.MiscSignalRDebug ? LogLevel.Error : LogLevel.Debug;

  const connectSigR = useCallback(() => {
    const hub = new HubConnectionBuilder().configureLogging(DebugSigR);

    if (settings.MessagePack) hub.withHubProtocol(new MessagePackHubProtocol());

    const sig = new SignalWrapper(hub)
      .withLock()
      .withStatusCallback((_, state) => {
        SetStatus(state);
        console.info("SignalR state changed!", state);

        switch (state) {
          case HubConnectionState.Connected:
            toast.success("Connection to SignalR successful");

            break;
          case HubConnectionState.Disconnected:
            toast.warning("Connection to SignalR lost, attempting to reconnect");
            SetUserStatus(TutorStatus.Offline);
            break;

          default:
            toast.info("SignalR state changed to: " + state);
            break;
        }
      })
      .withErrorCallback((_, err) => {
        SetStatus(HubConnectionState.Disconnected);
        SetUserStatus(TutorStatus.Offline);
        if (err.isSome()) {
          const error = err.unwrap();

          switch (error.statusCode) {
            case 401:
              Logout();
              break;

            case InternalSigRErrors.WebLockAlreadyAcquired:
              toast.error(
                "Warning: You already have another tutor dashboard tab open. This may cause issues. Please close duplicate tabs and refresh the page!",
                {
                  duration: 9999999,
                  closeButton: true,
                },
              );
              break;

            default:
              toastAndError("SigR error callback: " + error.message, error.statusCode);
              break;
          }
        }
      })
      .withDebugSetup()
      .withOnStartup(() => {
        Create(sig);

        sig
          .Invoke(SigRInvoke.JoinLessonDashboardGroup)
          .then(() => {
            log("Joined lesson dashboard group");
          })
          .catch((e) => toastAndError("Unable to connect to dashboard group", e));
      });

    sig.StartWithAutoConnect(userId, auth, 1000 * 10);

    return () => {
      sig.Dispose().catch(() => toastAndWarn("Unable to dispose of SignalR, this could be bad"));
      Remove();
    };
  }, [Logout, DebugSigR, Create, SetUserStatus, userId, SetStatus, auth, settings, Remove]);

  useEffect(() => {
    if (!PRODUCTION) {
      if (import.meta.hot) {
        // TLDR: Free the lock before we reload the page as to avoid web-lock spam messages when developing locally
        const freeLock = () => {
          signalr?.FreeLock();
        };

        const wsDisconnectAlert = () => {
          toast.warning("Vite websocket disconnected, hot reloading may not work");
        };

        const wsConnectedAlert = () => {
          toast.success("Vite websocket connected");
        };

        import.meta.hot.on("vite:beforeUpdate", freeLock);
        import.meta.hot.on("vite:ws:disconnect", wsDisconnectAlert);
        import.meta.hot.on("vite:ws:connect", wsConnectedAlert);

        return () => {
          if (import.meta.hot) {
            import.meta.hot.off("vite:beforeUpdate", freeLock);
            import.meta.hot.off("vite:ws:disconnect", wsDisconnectAlert);
            import.meta.hot.off("vite:ws:connect", wsConnectedAlert);
          }
        };
      }
    }
  }, [signalr]);

  useEffect(() => {
    Startup();
  }, [Startup]);

  useEffect(() => {
    if (valid && userId !== -1 && tutorId !== -1) {
      const remove = connectSigR();
      return () => {
        remove();
      };
    }

    if (valid === false && !window.location.pathname.includes("/auth/"))
      window.location.assign("/auth/login");
  }, [auth, userId, tutorId, valid, connectSigR]);

  return <>{children}</>;
};
